import type { IReadOnlyUploadManager, Listener } from '@core/transmission/IReadOnlyFileTransmissionManager'
import type { AbsUploadTask } from '@/core/transmission/AbsUploadTask'
import { UploadTaskDirectly } from '@/core/transmission/UploadTaskDirectly'
import { UploadTaskWithCloud } from '@core/transmission/UploadTaskWithCloud'
import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
import { AvailableType } from '../devices/IDevice'
import { AbsFileTransmissionManager } from '@core/transmission/AbsFileTransmissionManager'
import { INSTANCE as certificateManager, TransmissionType } from '@core/transmission/CertificateManager'
import { isEmpty } from '@core/Utils'
import { Env } from '@core/EnvUtils'
import { INSTANCE as DeviceConnectionKeeper } from '@core/devices/DeviceConnectionKeeper'
import { DeviceTaskError } from './ITask'

export class FileUploadManager extends AbsFileTransmissionManager<AbsUploadTask> {
  #listener: Listener | undefined
  constructor() {
    super()
  }

  /**
   *
   * @param appId
   * @param deviceId
   * @param targetPath
   * @param files
   * @param isViaCloud 是否走云传输 true 走云传输
   */
  async append(
    appId: string,
    deviceId: string,
    targetPath: string,
    files: File[],
    isViaCloud = false,
    relativePath = '',
    isSupportZip?: boolean
  ): Promise<void> {
    if (isEmpty(appId) || isEmpty(deviceId) || isEmpty(targetPath) || files.length === 0) {
      return
    }
    const device = DeviceManager.getDeviceById(deviceId)

    if (!device) return
    if (this._isDisableAppendTask(device)) return

    device.isSupportZip = isSupportZip ?? false
    const lanAvailable = await device.getLanAvailableInfo()

    if (lanAvailable === AvailableType.REACHABLE && !isViaCloud) {
      // 证书有效时
      this.#createDirectlyTask(deviceId, targetPath, files, true, relativePath, appId)
    } else {
      this.#createOcsTask(appId, deviceId, targetPath, files, relativePath)
    }

    if (lanAvailable === AvailableType.REACHABLE_ERROR_CERTIFICATION) {
      certificateManager.transmissionType = TransmissionType.FILE_UPLOAD
      certificateManager.showLANAvailabilityNotice({ extraQuery: `deviceId=${device.id}` })
    }
  }

  setTaskUploadListener(task: AbsUploadTask): void {
    task.setUploadListener({
      onError: (deviceId, targetDir, fileName, reason) => {
        try {
          if (reason === DeviceTaskError.DEVICE_CANCEL) {
            this._taskScheduler.removeTask(task, false, false)
          }
          this.#listener?.onError(deviceId, targetDir, fileName, reason)
        } catch (error) {
          // 在调用微应用回调的时候，主动抛出捕获错误，同时在release环境让运行错误不阻塞程序运行
          if (!Env.isRelease()) {
            throw error
          }
        }
      },
      onSuccess: (deviceId, targetDir, fileName) => {
        try {
          this.#listener?.onSuccess(deviceId, targetDir, fileName)
        } catch (error) {
          if (!Env.isRelease()) {
            throw error
          }
        }
      },
      onProgressChanged: (transmitted, total, deviceId, isTransmissionToOCS) => {
        // 根据进度通知端侧保持端口开启
        DeviceConnectionKeeper.keepDeviceConnectById(deviceId)
      },
    })
  }

  setListener(listener: Listener): void {
    this.#listener = listener
  }

  removeListener(): void {
    this.#listener = undefined
  }

  getManager(appId: string): IReadOnlyUploadManager {
    return {
      append: (
        deviceId: string,
        targetPath: string,
        files: File[],
        isViaCloud: boolean,
        relativePath: string,
        isSupportZip?: boolean,
        isSupportRange?: boolean
      ) => {
        this.append(appId, deviceId, targetPath, files, isViaCloud, relativePath, isSupportZip)
      },
      setListener: (listener: Listener) => this.setListener(listener),
      removeListener: () => this.removeListener(),
    }
  }

  protected _onSuccess(task: AbsUploadTask): void {
    // 上传成功后清理不再使用的File对象，降低内存占用
    task.cleanUpUnusedData()
  }

  #createDirectlyTask(
    deviceId: string,
    targetPath: string,
    files: File[],
    isFast: boolean,
    relativePath: string,
    appId: string
  ) {
    for (const file of files) {
      if (!file) continue
      const task = new UploadTaskDirectly(deviceId, targetPath, file, isFast, relativePath, appId)

      this._taskScheduler?.addTask(task)
      this.setTaskUploadListener(task)
    }
  }

  async #createOcsTask(appId: string, deviceId: string, targetPath: string, files: File[], relativePath: string) {
    for (const file of files) {
      if (!file) continue

      const task = new UploadTaskWithCloud(deviceId, targetPath, file, relativePath, appId)

      this._taskScheduler?.addTask(task)
      this.setTaskUploadListener(task)
    }
  }
}

const INSTANCE = new FileUploadManager()

export { INSTANCE }
